// Validation for the source-graph model. The gate that keeps the CORE RULE
// honest: an edge that cannot answer "what evidence proves this and where did
// it come from" must NOT render as a normal verified link (the query layer
// hides invalid edges; the trust layer downgrades incomplete ones).
//
// errors  = the record is unusable / dishonest as-is (exclude it).
// warnings = the record is usable but degraded (show it quietly, fix later).

import {
  isConfidence,
  isSourceCategory,
  type Evidence,
  type Passage,
  type SourceEdge,
  type Work,
} from "./sourceGraph.ts";
import { structuralBaseMatches } from "./normalizeRef.ts";

export type ValidationResult = {
  valid: boolean;
  errors: string[];
  warnings: string[];
};

const ok = (errors: string[], warnings: string[]): ValidationResult => ({
  valid: errors.length === 0,
  errors,
  warnings,
});

const isNonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

// ---------------------------------------------------------------------------

export function validateWork(work: Work): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!isNonEmpty(work.id)) errors.push("Work: missing id");
  if (!isNonEmpty(work.canonicalTitle)) errors.push(`Work ${work.id || "?"}: missing canonicalTitle`);
  if (!isSourceCategory(work.category)) errors.push(`Work ${work.id || "?"}: invalid category "${String(work.category)}"`);
  if (!isNonEmpty(work.heTitle)) warnings.push(`Work ${work.id || "?"}: missing Hebrew title`);
  return ok(errors, warnings);
}

export function validatePassage(passage: Passage): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const tag = `Passage ${passage.id || passage.canonicalRef || "?"}`;
  if (!isNonEmpty(passage.id)) errors.push("Passage: missing id");
  if (!isNonEmpty(passage.workId)) errors.push(`${tag}: missing workId`);
  if (!isNonEmpty(passage.canonicalRef)) errors.push(`${tag}: missing canonicalRef`);
  if (!passage.normalizedRef) errors.push(`${tag}: missing normalizedRef`);
  else if (passage.normalizedRef.confidence === "failed") errors.push(`${tag}: normalizedRef failed normalization`);
  if (!isNonEmpty(passage.status)) errors.push(`${tag}: missing status`);
  if (!isNonEmpty(passage.displayTitle)) errors.push(`${tag}: missing displayTitle`);
  if (passage.status === "invalid") errors.push(`${tag}: status is invalid`);
  if (!passage.heRef) warnings.push(`${tag}: missing Hebrew ref`);
  if (passage.textAvailable && !passage.text?.he && !passage.text?.en)
    warnings.push(`${tag}: textAvailable is true but no text is attached`);
  return ok(errors, warnings);
}

export function validateSourceEdge(edge: SourceEdge): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const tag = `Edge ${edge.id || "?"} (${edge.fromRef || "?"} -> ${edge.toRef || "?"})`;
  if (!isNonEmpty(edge.id)) errors.push("Edge: missing id");
  if (!isNonEmpty(edge.fromPassageId)) errors.push(`${tag}: missing fromPassageId`);
  if (!isNonEmpty(edge.toPassageId)) errors.push(`${tag}: missing toPassageId`);
  if (!isNonEmpty(edge.relationshipType)) errors.push(`${tag}: missing relationshipType`);
  if (!isNonEmpty(edge.edgeClass)) errors.push(`${tag}: missing edgeClass`);
  if (!isConfidence(edge.confidence)) errors.push(`${tag}: invalid confidence "${String(edge.confidence)}"`);
  if (!Array.isArray(edge.evidenceIds)) errors.push(`${tag}: missing evidenceIds array`);
  if (!edge.provenance || !isNonEmpty(edge.provenance.origin)) errors.push(`${tag}: missing provenance`);
  if (!isNonEmpty(edge.visibility)) errors.push(`${tag}: missing visibility`);

  // Honesty cross-checks (the load-bearing ones):
  if (Array.isArray(edge.evidenceIds) && edge.evidenceIds.length === 0) {
    if (edge.confidence === "verified" || edge.confidence === "high") {
      errors.push(`${tag}: claims ${edge.confidence} confidence with NO evidence`);
    } else {
      warnings.push(`${tag}: has no evidence - must render as unverified`);
    }
  }
  if (edge.provenance?.origin === "ai_suggested" && edge.confidence === "verified") {
    errors.push(`${tag}: AI-suggested edges can never be confidence "verified"`);
  }
  if (edge.edgeClass === "ai" && (edge.confidence === "verified" || edge.confidence === "high")) {
    errors.push(`${tag}: ai edgeClass cannot carry ${edge.confidence} confidence`);
  }
  // A STRUCTURAL comments_on claim must be supported by the ref shape itself:
  // fromRef is "X on <base>" and <base> is the toRef (segment-depth tolerant).
  // Anything else claiming structural is asserting proof it does not have.
  if (edge.edgeClass === "structural" && edge.relationshipType === "comments_on") {
    if (!structuralBaseMatches(edge.fromRef ?? "", edge.toRef ?? "")) {
      errors.push(`${tag}: structural comments_on claim is not supported by the ref shape`);
    }
  }
  return ok(errors, warnings);
}

export function validateEvidence(evidence: Evidence): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const tag = `Evidence ${evidence.id || "?"}`;
  if (!isNonEmpty(evidence.id)) errors.push("Evidence: missing id");
  if (!isNonEmpty(evidence.edgeId)) errors.push(`${tag}: missing edgeId`);
  if (!isNonEmpty(evidence.evidenceType)) errors.push(`${tag}: missing evidenceType`);

  switch (evidence.evidenceType) {
    case "provider_link":
      if (!evidence.provider) errors.push(`${tag}: provider_link evidence must name its provider`);
      break;
    case "quoted_text":
    case "phrase_match":
      if (!isNonEmpty(evidence.quoteText) && !isNonEmpty(evidence.heQuoteText))
        warnings.push(`${tag}: quote evidence should carry quoteText or heQuoteText`);
      break;
    case "explicit_ref":
      if (!isNonEmpty(evidence.parserName) && !isNonEmpty(evidence.explanation))
        warnings.push(`${tag}: explicit_ref evidence should say how the ref was detected`);
      break;
    case "ai_explanation":
      // AI output is explanation, never proof. An AI evidence record scoring
      // itself as certain is pretending to be proof - reject that.
      if (evidence.provider !== "AI") errors.push(`${tag}: ai_explanation evidence must set provider to "AI"`);
      if ((evidence.confidenceScore ?? 0) >= 1)
        errors.push(`${tag}: ai_explanation may not claim certainty (confidenceScore 1)`);
      break;
    default:
      break;
  }
  return ok(errors, warnings);
}
